import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import OwnerPageLayout from '../../components/layout/OwnerPageLayout';
import { getApiBaseUrl } from '../../apiBase';
import './remote-config.css';

const CATEGORIES = ['notifications', 'app', 'servers', 'features', 'maintenance'];

function adminApi() {
    return axios.create({
        baseURL: getApiBaseUrl(),
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    });
}

function configMapFromList(rows) {
    const map = {};
    (rows || []).forEach((r) => {
        map[r.key] = r.value;
    });
    return map;
}

function QuickAction({ icon, label, checked, onChange, danger }) {
    return (
        <div className={`quick-action${danger ? ' quick-action--danger' : ''}`}>
            <span>{icon} {label}</span>
            <label className="toggle">
                <input
                    type="checkbox"
                    checked={checked === 'true' || checked === true}
                    onChange={(e) => onChange(e.target.checked ? 'true' : 'false')}
                />
                <span className="toggle-slider" />
            </label>
        </div>
    );
}

function ConfigItem({ configKey, value, onSave, saving }) {
    const [localValue, setLocalValue] = useState(value);
    const isBool = value === 'true' || value === 'false';

    useEffect(() => {
        setLocalValue(value);
    }, [value]);

    if (isBool) {
        return (
            <div className="config-item">
                <div className="config-key"><code>{configKey}</code></div>
                <label className="toggle">
                    <input
                        type="checkbox"
                        checked={localValue === 'true'}
                        onChange={(e) => {
                            const v = e.target.checked ? 'true' : 'false';
                            setLocalValue(v);
                            onSave(v);
                        }}
                    />
                    <span className="toggle-slider" />
                </label>
                {saving && <span>💾</span>}
            </div>
        );
    }

    return (
        <div className="config-item">
            <div className="config-key"><code>{configKey}</code></div>
            <div className="config-value-row">
                <input
                    value={localValue ?? ''}
                    onChange={(e) => setLocalValue(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && onSave(localValue)}
                />
                <button
                    type="button"
                    className="sp-btn sp-btn--ghost"
                    onClick={() => onSave(localValue)}
                    disabled={saving || localValue === value}
                >
                    {saving ? '…' : '💾'}
                </button>
            </div>
        </div>
    );
}

export default function RemoteConfigPage() {
    const { t } = useTranslation(['adminConfig', 'nav', 'common']);
    const [configs, setConfigs] = useState({});
    const [history, setHistory] = useState([]);
    const [saving, setSaving] = useState(null);
    const [loading, setLoading] = useState(true);
    const [msg, setMsg] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const api = adminApi();
            const [cfgRes, histRes] = await Promise.all([
                api.get('/admin/config'),
                api.get('/admin/config/history?limit=30'),
            ]);
            setConfigs(configMapFromList(cfgRes.data));
            setHistory(histRes.data || []);
        } catch {
            setMsg(t('loadError'));
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => {
        load();
    }, [load]);

    const updateConfig = useCallback(async (key, value) => {
        setSaving(key);
        setMsg('');
        try {
            await adminApi().put(`/admin/config/${encodeURIComponent(key)}`, { value });
            setConfigs((prev) => ({ ...prev, [key]: value }));
            setMsg(t('saved'));
            const histRes = await adminApi().get('/admin/config/history?limit=30');
            setHistory(histRes.data || []);
        } catch {
            setMsg(t('saveError'));
        } finally {
            setSaving(null);
        }
    }, [t]);

    const setLocalConfig = useCallback((key, value) => {
        setConfigs((prev) => ({ ...prev, [key]: value }));
    }, []);

    const testServer = useCallback(async (url) => {
        if (!url) return;
        const base = url.replace(/\/$/, '');
        try {
            const res = await axios.get(`${base}/api/health`, { timeout: 5000 });
            setMsg(res.data?.status === 'ok' ? `${t('serverOk')}: ${base}` : t('serverFail'));
        } catch {
            setMsg(`${t('serverFail')}: ${base}`);
        }
    }, [t]);

    const saveBulk = useCallback(async (keys) => {
        const updates = {};
        keys.forEach((k) => {
            if (configs[k] !== undefined) updates[k] = configs[k];
        });
        setSaving('bulk');
        try {
            await adminApi().post('/admin/config/bulk', updates);
            setMsg(t('saved'));
            await load();
        } catch {
            setMsg(t('saveError'));
        } finally {
            setSaving(null);
        }
    }, [configs, load, t]);

    const categoryItems = useMemo(() => {
        const result = {};
        CATEGORIES.forEach((cat) => {
            result[cat] = Object.entries(configs).filter(([key]) => key.startsWith(`${cat}.`));
        });
        return result;
    }, [configs]);

    if (loading) {
        return (
            <OwnerPageLayout title={t('title')}>
                <p>{t('common:loading')}</p>
            </OwnerPageLayout>
        );
    }

    return (
        <OwnerPageLayout title={t('title')}>
            <div className="remote-config-page">
                <div className="page-header">
                    <h1>⚙️ {t('title')}</h1>
                    <p className="subtitle">{t('subtitle')}</p>
                    <div className="live-badge">🟢 {t('liveChanges')}</div>
                </div>

                {msg && (
                    <div className={`msg ${msg.includes(t('saveError')) || msg.includes(t('loadError')) ? 'msg--err' : 'msg--ok'}`}>
                        {msg}
                    </div>
                )}

                <h3>{t('quickActions')}</h3>
                <div className="quick-actions">
                    <QuickAction
                        icon="🔧"
                        label={t('maintenanceMode')}
                        checked={configs['maintenance.enabled']}
                        onChange={(v) => updateConfig('maintenance.enabled', v)}
                        danger
                    />
                    <QuickAction
                        icon="📱"
                        label={t('forceUpdate')}
                        checked={configs['app.force_update']}
                        onChange={(v) => updateConfig('app.force_update', v)}
                        danger
                    />
                    <QuickAction
                        icon="🔔"
                        label="WebSocket"
                        checked={configs['notifications.websocket_enabled']}
                        onChange={(v) => updateConfig('notifications.websocket_enabled', v)}
                    />
                    <QuickAction
                        icon="💬"
                        label="Push"
                        checked={configs['notifications.push_enabled']}
                        onChange={(v) => updateConfig('notifications.push_enabled', v)}
                    />
                </div>

                {CATEGORIES.map((cat) => (
                    <div key={cat} className="config-section">
                        <div className="section-header">
                            <h2>{t(`categories.${cat}`)}</h2>
                        </div>
                        <div className="config-items">
                            {categoryItems[cat]?.map(([key, value]) => (
                                <ConfigItem
                                    key={key}
                                    configKey={key}
                                    value={value}
                                    onSave={(v) => updateConfig(key, v)}
                                    saving={saving === key}
                                />
                            ))}
                        </div>
                    </div>
                ))}

                <div className="config-section">
                    <div className="section-header">
                        <h2>🌐 {t('serverManagement')}</h2>
                        <p>{t('serversNote')}</p>
                    </div>
                    <div className="servers-grid">
                        {['ru', 'intl'].map((region) => (
                            <div key={region} className="server-card">
                                <h3>{region === 'ru' ? '🇷🇺 RU' : '🌍 INTL'}</h3>
                                <label>{t('primaryServer')}</label>
                                <div className="server-input-row">
                                    <input
                                        value={configs[`servers.${region}_primary`] || ''}
                                        onChange={(e) => setLocalConfig(`servers.${region}_primary`, e.target.value)}
                                    />
                                    <button type="button" className="sp-btn sp-btn--ghost" onClick={() => testServer(configs[`servers.${region}_primary`])}>
                                        📡
                                    </button>
                                    <button type="button" className="sp-btn sp-btn--primary" onClick={() => updateConfig(`servers.${region}_primary`, configs[`servers.${region}_primary`] || '')}>
                                        💾
                                    </button>
                                </div>
                                <label style={{ marginTop: 12 }}>{t('backupServer')}</label>
                                <div className="server-input-row">
                                    <input
                                        value={configs[`servers.${region}_backup`] || ''}
                                        placeholder="—"
                                        onChange={(e) => setLocalConfig(`servers.${region}_backup`, e.target.value)}
                                    />
                                    <button type="button" className="sp-btn sp-btn--ghost" onClick={() => testServer(configs[`servers.${region}_backup`])}>
                                        📡
                                    </button>
                                    <button type="button" className="sp-btn sp-btn--primary" onClick={() => updateConfig(`servers.${region}_backup`, configs[`servers.${region}_backup`] || '')}>
                                        💾
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="config-section">
                    <div className="section-header">
                        <h2>📱 {t('versionManagement')}</h2>
                    </div>
                    <div className="versions-grid">
                        <div className="version-card">
                            <h3>🤖 Android</h3>
                            <label>{t('minVersion')}</label>
                            <input
                                className="sp-input"
                                value={configs['app.min_version_android'] || ''}
                                onChange={(e) => setLocalConfig('app.min_version_android', e.target.value)}
                            />
                            <label style={{ marginTop: 10 }}>{t('apkUrl')}</label>
                            <input
                                className="sp-input"
                                value={configs['app.update_url_android'] || ''}
                                onChange={(e) => setLocalConfig('app.update_url_android', e.target.value)}
                            />
                            <span className="hint">{t('apkHint')}</span>
                        </div>
                        <div className="version-card">
                            <h3>🍎 iOS</h3>
                            <label>{t('minVersion')}</label>
                            <input
                                className="sp-input"
                                value={configs['app.min_version_ios'] || ''}
                                onChange={(e) => setLocalConfig('app.min_version_ios', e.target.value)}
                            />
                            <label style={{ marginTop: 10 }}>{t('iosUrl')}</label>
                            <input
                                className="sp-input"
                                value={configs['app.update_url_ios'] || ''}
                                onChange={(e) => setLocalConfig('app.update_url_ios', e.target.value)}
                            />
                            <span className="hint">{t('iosHint')}</span>
                        </div>
                    </div>
                    <label style={{ display: 'block', marginTop: 16 }}>{t('updateMessage')}</label>
                    <textarea
                        className="sp-input"
                        rows={3}
                        value={configs['app.update_message'] || ''}
                        onChange={(e) => setLocalConfig('app.update_message', e.target.value)}
                    />
                    <button
                        type="button"
                        className="sp-btn sp-btn--primary"
                        style={{ marginTop: 12 }}
                        disabled={saving === 'bulk'}
                        onClick={() => saveBulk([
                            'app.min_version_android',
                            'app.min_version_ios',
                            'app.latest_version',
                            'app.update_url_android',
                            'app.update_url_ios',
                            'app.update_message',
                        ])}
                    >
                        💾 {t('saveSettings')}
                    </button>
                </div>

                <div className="config-section">
                    <div className="section-header">
                        <h2>🔧 {t('categories.maintenance')}</h2>
                    </div>
                    <label className="toggle" style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                        <input
                            type="checkbox"
                            checked={configs['maintenance.enabled'] === 'true'}
                            onChange={(e) => updateConfig('maintenance.enabled', e.target.checked ? 'true' : 'false')}
                        />
                        <span>
                            {configs['maintenance.enabled'] === 'true' ? `🔴 ${t('maintenanceOn')}` : `🟢 ${t('maintenanceOff')}`}
                        </span>
                    </label>
                    <label>{t('maintenanceMessage')}</label>
                    <textarea
                        className="sp-input"
                        rows={2}
                        value={configs['maintenance.message'] || ''}
                        onChange={(e) => setLocalConfig('maintenance.message', e.target.value)}
                    />
                    <label style={{ marginTop: 10 }}>{t('maintenanceEnds')}</label>
                    <input
                        type="datetime-local"
                        className="sp-input"
                        value={configs['maintenance.ends_at'] || ''}
                        onChange={(e) => setLocalConfig('maintenance.ends_at', e.target.value)}
                    />
                    <button
                        type="button"
                        className="sp-btn sp-btn--primary"
                        style={{ marginTop: 12 }}
                        onClick={() => saveBulk(['maintenance.message', 'maintenance.ends_at'])}
                    >
                        💾 {t('saveSettings')}
                    </button>
                </div>

                <div className="config-section">
                    <h2>📋 {t('history')}</h2>
                    <table className="config-history">
                        <thead>
                            <tr>
                                <th>Key</th>
                                <th>{t('was')}</th>
                                <th>{t('became')}</th>
                                <th>{t('who')}</th>
                                <th>{t('when')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {history.map((h) => (
                                <tr key={h.id}>
                                    <td><code>{h.key}</code></td>
                                    <td className="old">{h.oldValue}</td>
                                    <td className="new">{h.newValue}</td>
                                    <td>{h.updatedBy}</td>
                                    <td>{new Date(h.updatedAt).toLocaleString()}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </OwnerPageLayout>
    );
}
